import { Droplets, Palette, Shield } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { ScrollReveal } from "@/components/ScrollAnimations";

export const AboutSection = ({ settings }) => {
  const { t } = useLang();
  const aboutText = settings?.about_text || t("about_text");

  const features = [
    {
      icon: Droplets,
      title: t("about_feat_custom"),
      description: t("about_feat_custom_desc"),
    },
    {
      icon: Shield,
      title: t("about_feat_safe"),
      description: t("about_feat_safe_desc"),
    },
    {
      icon: Palette,
      title: t("about_feat_styles"),
      description: t("about_feat_styles_desc"),
    },
  ];

  return (
    <section id="about" className="py-24 px-6 relative overflow-hidden" data-testid="about-section">

      {/* Decorative line */}
      <div className="absolute left-1/2 top-0 w-px h-24 bg-gradient-to-b from-transparent to-purple-500/20" />

      <div
        className="absolute top-0 right-0 w-96 h-96 opacity-30 pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(219, 39, 119, 0.1), transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          <div>
            <ScrollReveal direction="left" duration={900}>
              <p
                className="text-xs tracking-[0.3em] uppercase font-bold text-pink-500 mb-3"
                style={{ fontFamily: "'Cinzel Decorative', serif" }}
              >
                {t("about_subtitle")}
              </p>
            </ScrollReveal>
            <ScrollReveal direction="left" delay={100} duration={900}>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-8" data-testid="about-title">
                {t("about_title_1")}
                <br />
                <span className="text-pink-500 neon-text-pink">{t("about_title_2")}</span>
              </h2>
            </ScrollReveal>
            <ScrollReveal direction="left" delay={200} duration={900}>
              <p className="text-base text-neutral-400 leading-relaxed mb-6" data-testid="about-description">
                {aboutText}
              </p>
            </ScrollReveal>
            <ScrollReveal direction="left" delay={300} duration={900}>
              <p className="text-base text-neutral-500 leading-relaxed">
                {t("about_body2")}
              </p>
            </ScrollReveal>
          </div>

          <div className="space-y-6">
            {features.map((feature, idx) => (
              <ScrollReveal key={feature.title} direction="right" delay={idx * 150} duration={800}>
                <div
                  className="glass-card p-6 rounded-xl hover:border-purple-500/20 transition-all duration-500 group"
                  data-testid={`about-feature-${feature.title}`}
                >
                  <div className="flex gap-4">
                    <div className="shrink-0 w-10 h-10 flex items-center justify-center bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors duration-300">
                      <feature.icon className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{feature.title}</h3>
                      <p className="text-sm text-neutral-400 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
